<template>
  <div class="mobile-home-container">
    <!-- Hero Section -->
    <section class="relative h-64 overflow-hidden">
      <video 
        :src="homeVideo" 
        autoplay
        muted 
        playsinline 
        loop 
        preload="auto" 
        class="w-full h-full object-cover block video-background"
        webkit-playsinline
        x5-playsinline
        x5-video-player-type="h5"
        x5-video-player-fullscreen="true"
        x5-video-orientation="portraint"
      ></video>
      <div class="absolute inset-0 bg-black bg-opacity-40"></div>
      <div class="absolute inset-0 flex items-center px-4 z-20">
        <div>
          <h1 class="text-white text-[4vw] font-bold mb-2 leading-relaxed">{{ homeTexts.hero.title }}</h1>
        </div>
      </div>
    </section>

    <!-- Who We Are Section -->
    <section class="bg-gradient-to-b from-[#010204] to-[#0d0971] py-8 px-4">
      <div class="flex flex-row items-center gap-4 mb-6">
        <!-- Map Image -->
        <div class="w-[55%] relative">
          <img src="@/assets/map.png" class="w-full h-auto" />
          <img v-for="(img, index) in smallImages" :key="index" :src="img.src"
            class="absolute w-6 h-8 small-image" :style="img.style" />
        </div>
        
        <!-- Content -->
        <div class="w-[45%] text-white">
          <h2 class="text-[6vw] font-bold mb-4">
            <span class="text-[#ff8c00]">{{ homeTexts.whoWeAre.title.who }}</span>
            <span class="ml-2">{{ homeTexts.whoWeAre.title.weAre }}</span>
          </h2>
          <p class="text-[3.5vw] leading-relaxed mb-4">
            <span class="font-bold text-[#ff8c00]">Creston Group</span>
            {{ homeTexts.whoWeAre.description }}
          </p>
        </div>
      </div>
      
      <!-- Three Cards -->
      <div class="grid grid-cols-3 gap-3">
        <div class="bg-[#FFA200] p-2 rounded-lg">
          <div class="flex flex-col items-start mb-2">
            <div class="w-8 h-8 bg-black rounded-full flex items-center justify-center mb-2">
              <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707L16 7.586A1 1 0 0015.414 7H14z"/>
              </svg>
            </div>
            <h3 class="font-bold text-black text-[3vw]">{{ homeTexts.whoWeAre.bulletPoints.deliver.title.replace(/–$/, '') }}</h3>
          </div>
          <p class="text-black text-[2.5vw] leading-relaxed">{{ homeTexts.whoWeAre.bulletPoints.deliver.content }}</p>
        </div>
        
        <div class="bg-[#FFA200] p-2 rounded-lg">
          <div class="flex flex-col items-start mb-2">
            <div class="w-8 h-8 bg-black rounded-full flex items-center justify-center mb-2">
              <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z"/>
              </svg>
            </div>
            <h3 class="font-bold text-black text-[3vw]">{{ homeTexts.whoWeAre.bulletPoints.grow.title.replace(/–$/, '') }}</h3>
          </div>
          <p class="text-black text-[2.5vw] leading-relaxed">{{ homeTexts.whoWeAre.bulletPoints.grow.content }}</p>
        </div>
        
        <div class="bg-[#FFA200] p-2 rounded-lg">
          <div class="flex flex-col items-start mb-2">
            <div class="w-8 h-8 bg-black rounded-full flex items-center justify-center mb-2">
              <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"/>
              </svg>
            </div>
            <h3 class="font-bold text-black text-[3vw]">{{ homeTexts.whoWeAre.bulletPoints.last.title.replace(/–$/, '') }}</h3>
          </div>
          <p class="text-black text-[2.5vw] leading-relaxed">{{ homeTexts.whoWeAre.bulletPoints.last.content }}</p>
        </div>
      </div>
    </section>

    <!-- What We Offer Section -->
    <section class="py-8 px-4 relative bg-white">
      <div class="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20" :style="{ backgroundImage: `url(${bgOfferImage})`, backgroundSize: '100% auto' }"></div>
      <div class="relative z-10">
        <h2 class="text-[6vw] font-bold text-left mb-6">
          <span class="text-[#ff8c00]">{{ homeTexts.offerings.title.what }}</span>
          <span class="text-black ml-2">{{ homeTexts.offerings.title.weOffer }}</span>
        </h2>
        
        <!-- Top two cards -->
        <div class="flex gap-3 mb-4">
          <div class="bg-[#0a0a7a] text-white p-3 rounded-lg relative w-[45%]">
            <h3 class="text-[3.5vw] font-bold mb-2">{{ homeTexts.offerings.sourcingFromChina.title }}</h3>
            <p class="text-[2.5vw] leading-relaxed">{{ homeTexts.offerings.sourcingFromChina.description }}</p>
            <!-- Speech bubble tail -->
            <div class="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div class="w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent border-t-[#0a0a7a]"></div>
            </div>
          </div>
          
          <div class="bg-[#1e2a78] text-white p-3 rounded-lg relative w-[55%]">
            <h3 class="text-[3.5vw] font-bold mb-2">{{ homeTexts.offerings.salesServiceRepresentation.title }}</h3>
            <p class="text-[2.5vw] leading-relaxed">{{ homeTexts.offerings.salesServiceRepresentation.description }}</p>
            <!-- Speech bubble tail -->
            <div class="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
              <div class="w-0 h-0 border-l-4 border-r-4 border-t-6 border-l-transparent border-r-transparent border-t-[#1e2a78]"></div>
            </div>
          </div>
        </div>
        
        <!-- Middle section with truck and side services -->
        <div class="relative mb-4">
          <!-- Truck image centered -->
          <div class="flex justify-center mb-4">
            <img src="@/assets/care.png" class="w-32 h-auto" />
          </div>
          
          <!-- Side services below truck -->
          <div class="flex justify-between items-start px-4 mb-4">
            <div class="text-center w-1/2 pr-2">
              <div class="bg-white p-3 rounded-lg relative shadow-sm">
                <!-- 向上的白色尖角 -->
                <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full">
                  <div class="w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-white"></div>
                </div>
                <p class="text-[2.5vw] font-semibold text-black">{{ homeTexts.offerings.engineeringSupport.title }}</p>
              </div>
            </div>
            
            <div class="text-center w-1/2 pl-2">
              <div class="bg-white p-3 rounded-lg relative shadow-sm">
                <!-- 向上的白色尖角 -->
                <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full">
                  <div class="w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-white"></div>
                </div>
                <p class="text-[2.5vw] font-semibold text-black">{{ homeTexts.offerings.aftermarketServices.title }}</p>
              </div>
            </div>
          </div>
          
          <!-- Bottom Integrated Logistics card - 50% width and centered -->
          <div class="flex justify-center">
            <div class="bg-[#f59e0b] text-black p-4 rounded-lg text-center w-1/2 relative">
              <!-- 向上的橙色尖角 -->
              <div class="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-full">
                <div class="w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-[#f59e0b]"></div>
              </div>
              <h3 class="text-[4vw] font-bold mb-2">{{ homeTexts.offerings.integratedLogistics.title }}</h3>
              <p class="text-[3vw] leading-relaxed">{{ homeTexts.offerings.integratedLogistics.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Action Buttons -->
    <ActionButtons backgroundColor="#ffffff" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { pushToDataLayer } from '../utils/gtm';
import { useChatWidget } from '../utils/chatWidget';
import homeVideo from "@/assets/home.mp4";
import bgOfferImage from "@/assets/bg_offer.png";
import { generateSmallImagesForMobile } from "../utils/imageData";
import ActionButtons from "./ActionButtons.vue";

const router = useRouter();
const { locale, messages } = useI18n();

const dotSrc = new URL("@/assets/map_dot.png", import.meta.url).href;
const smallImages = generateSmallImagesForMobile(dotSrc);

// 页面加载后触发动画
onMounted(() => {
  setTimeout(() => {
    const smallImageElements = document.querySelectorAll('.small-image');
    smallImageElements.forEach((el) => {
      el.classList.add('animate');
    });
  }, 500); // 延迟500ms开始动画
});

const homeTexts = computed(() => {
  return messages.value[locale.value]?.home || {
    hero: { title: 'Default Title' },
    whoWeAre: {
      title: { who: 'Who', weAre: 'We Are' },
      description: 'Description',
      bulletPoints: {
        deliver: { title: 'Deliver', content: 'Content' },
        grow: { title: 'Grow', content: 'Content' },
        last: { title: 'Last', content: 'Content' }
      }
    },
    offerings: { 
      title: { what: 'What', weOffer: 'We Offer' },
      sourcingFromChina: { title: 'Title', description: 'Description' },
      salesServiceRepresentation: { title: 'Title', description: 'Description' },
      engineeringSupport: { title: 'Engineering Support' },
      aftermarketServices: { title: 'Aftermarket Services' },
      integratedLogistics: { title: 'Title', description: 'Description' }
    }
  };
});

const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("Home");
  pushToDataLayer("Chat_With_Us", {
    category: "Home",
    label: "Chat With Us",
  });
};
</script>

<style scoped>
.mobile-home-container {
  width: 100%;
  min-height: 100vh;
  position: relative;
}

/* 隐藏视频控制按钮 */
.video-background {
  pointer-events: none; /* 禁用视频交互 */
}

.video-background::-webkit-media-controls {
  display: none !important;
}

.video-background::-webkit-media-controls-panel {
  display: none !important;
}

.video-background::-webkit-media-controls-play-button {
  display: none !important;
}

.video-background::-webkit-media-controls-start-playback-button {
  display: none !important;
}

/* 确保视频区域正常滚动 */
section {
  position: relative;
  z-index: auto;
}

.small-image {
  opacity: 0;
  animation-fill-mode: forwards;
}

.small-image.animate {
  animation-name: smallImageAnimation;
  animation-duration: 1.5s;
  animation-timing-function: ease;
  animation-fill-mode: forwards;
}

@keyframes smallImageAnimation {
  0% {
    opacity: 0;
    transform: translateY(-20px) scale(1);
  }
  40% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  70% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
